import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Column } from 'primereact/column'
import { Dropdown } from 'primereact/dropdown'
import Button from '../../../components/ui/Button'
import DataTable from '../../../components/ui/DataTable'
import EmptyState from '../../../components/ui/EmptyState'
import { confirmDialog } from '../../../components/ui/ConfirmDialog'
import { useToast } from '../../../components/ui/ToastProvider'
import { useGroups, useUserGroups } from '../hooks/useGroups'
import type { Group, UserWithRoles } from '../types'

interface Props {
  user: UserWithRoles
  /** Salt okunur kip (126, Kısıtlı Yetkili asistan): grup listesi okunur; ekleme ve çıkarma çizilmez. */
  readOnly?: boolean
}

/**
 * Bir kullanıcının dahil olduğu gruplar (122) — Kullanıcı Yönetimi'nde satır açılınca görünür (masaüstü)
 * ya da kartta "Gruplar" ile açılır (dar ekran). DVA'daki Kullanıcı Yönetimi'nin karşılığı: grup listesi,
 * başlıkta grup seçip **Gruba Ekle**, her satırda onaylı **Gruptan Çıkar**.
 *
 * <p>Grup Yönetimi'ndeki üye paneliyle ({@link GroupMembersPanel}) <b>aynı kayıtları</b> yazar (aynı uçlar,
 * aynı önbellek): birinde yapılan değişiklik diğerinde de görünür. Seçici yalnız kullanıcının henüz üyesi
 * olmadığı grupları sunar.
 */
export default function UserGroupsPanel({ user, readOnly = false }: Readonly<Props>) {
  const { t, i18n } = useTranslation()
  const en = i18n.language.startsWith('en')
  const toast = useToast()
  const { list, addMember, removeMember } = useGroups()
  const memberOf = useUserGroups(user.id)
  const [selected, setSelected] = useState<number | null>(null)

  const nameOf = (g: Group) => (en && g.nameEn ? g.nameEn : g.nameTr)
  const ids = new Set(memberOf.data ?? [])
  const catalog = list.data ?? []
  const byName = (a: Group, b: Group) => nameOf(a).localeCompare(nameOf(b), en ? 'en' : 'tr')
  const rows = catalog.filter((g) => ids.has(g.id)).sort(byName)
  const options = catalog
    .filter((g) => !ids.has(g.id))
    .sort(byName)
    .map((g) => ({ label: nameOf(g), value: g.id }))
  const fullName = [user.name, user.surname].filter(Boolean).join(' ') || user.userName || `#${user.id}`

  const add = () => {
    if (selected == null) return
    addMember.mutate(
      { groupId: selected, userId: user.id },
      {
        onSuccess: () => {
          toast.show({ severity: 'success', summary: t('admin.users.groupsPanel.added'), life: 3000 })
          setSelected(null)
        },
      },
    )
  }

  const confirmRemove = (g: Group) =>
    confirmDialog({
      header: t('admin.groups.members.removeTitle'),
      message: t('admin.groups.members.removeConfirm', { name: fullName, group: nameOf(g) }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('admin.groups.members.remove'),
      accept: () =>
        removeMember.mutate(
          { groupId: g.id, userId: user.id },
          {
            onSuccess: () =>
              toast.show({ severity: 'success', summary: t('admin.groups.members.removed'), life: 3000 }),
          },
        ),
    })

  return (
    <div className="admin-group-members">
      <div className="admin-group-members-head">
        <h3 className="admin-group-members-title">{t('admin.users.groupsPanel.title')}</h3>
        {!readOnly && (
          <div className="admin-group-members-add">
            <Dropdown
              className="admin-user-group-select"
              value={selected}
              options={options}
              onChange={(e) => setSelected((e.value as number | null) ?? null)}
              placeholder={t('admin.users.groupsPanel.selectPlaceholder')}
              aria-label={t('admin.users.groupsPanel.selectPlaceholder')}
              emptyMessage={t('admin.users.groupsPanel.noMoreGroups')}
              showClear
            />
            <Button
              icon="pi pi-plus"
              label={t('admin.users.groupsPanel.add')}
              disabled={selected == null || addMember.isPending}
              loading={addMember.isPending}
              onClick={add}
            />
          </div>
        )}
      </div>
      <DataTable
        value={rows}
        loading={memberOf.isLoading || list.isLoading}
        dataKey="id"
        paginator={false}
        emptyMessage={<EmptyState title={t('admin.users.groupsPanel.empty')} />}
      >
        <Column header={t('admin.groups.columns.name')} body={(g: Group) => nameOf(g)} />
        {/* Salt okunur kipte tek eylemi (Gruptan Çıkar) kalkan kolon bütünüyle çizilmez. */}
        {!readOnly && (
          <Column
            header={t('admin.groups.columns.actions')}
            style={{ width: '6rem' }}
            body={(g: Group) => (
              <Button
                icon="pi pi-trash"
                rounded
                text
                severity="danger"
                disabled={removeMember.isPending}
                aria-label={t('admin.groups.members.remove')}
                tooltip={t('admin.groups.members.remove')}
                // Tablonun en sağındaki kolon: sağa açılan tooltip ekran dışına taşardı.
                tooltipOptions={{ position: 'bottom' }}
                onClick={() => confirmRemove(g)}
              />
            )}
          />
        )}
      </DataTable>
    </div>
  )
}
